import React, { useEffect, useState } from 'react'
import { formatDateYYYYMMDD } from '../../../../packages/utils/numberFormatters'
import { FormProvider, useForm } from 'react-hook-form'
import DocsApi from '../../../../api/DocsApi'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import { SourceTypeSelect } from './AdminSourceTypeSelect'
import { RegisteredDatePicker } from '../../../../components/fields/DatePicker'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { SourceType } from 'types/SourceType'
import { singularIssueSourceTypes, singularWarrantSourceTypes } from '../../../../utils/issues/sourceTypes'
import { intersection } from './utils'
import { SourceForm } from '../../types/SourceForm'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { useTranslation } from 'react-i18next'
import { WarrantListItem } from '@finance-lab/utils/types'

// here is where we build the new, proper, filename, according to github ticket #5
function setGoodFilename(
    entity: {
        symbol?: string | null
        shareName?: string | null
        instrumentSymbol?: string | null
        name?: string | null
    },
    sourceType: SourceType,
    publicationDate: Date | string | null,
) {
    let symbol: string | null = null
    const props = ['symbol', 'shareName', 'instrumentSymbol', 'name'] as const

    for (let i = 0; i < props.length; i++) {
        const property = props[i]
        if (property in entity && entity[property]) {
            symbol = entity[property]
            break
        }
    }

    // check presence of all necessary context variables
    if (!symbol || !sourceType) {
        throw new Error('Missing context variables for filename')
    }
    const translationMap: Partial<Record<SourceType, string>> = {
        Prospectus: 'Prospekt',
        InformationDocument: 'Informationsdokument',
    }
    symbol = symbol.replace(/ /g, '-') // replace space with dash
    const translatedSourceType = sourceType in translationMap ? translationMap[sourceType] : sourceType

    const newFilename = `${symbol}_${
        publicationDate ? `${formatDateYYYYMMDD(publicationDate, { disableNonBreakingDash: true })}_` : ''
    }${translatedSourceType}.pdf`
    return newFilename
}

export type UploadedSourceDialogProps = {
    open: boolean
    setOpen: (val: boolean) => void
    handleSubmit: (url: string, file: File, formData: SourceForm) => Promise<void>
    publicationDate: string | Date | null
    subStartDate: string | Date | null
    issue: FormIssueEntity | null
    warrant?: WarrantListItem
}

export function UploadedSourceDialog({
    open,
    setOpen,
    handleSubmit,
    issue,
    publicationDate,
    subStartDate,
    warrant,
}: UploadedSourceDialogProps) {
    const { t } = useTranslation()
    const formMethods = useForm<SourceForm>({ defaultValues: { title: '' } })
    const [file, setFile] = useState<File | null>(null)
    const [saving, setSaving] = useState(false)
    const docsApi = new DocsApi()
    const entity = issue ?? warrant

    useEffect(() => {
        return () => {
            if (!open) formMethods.reset()
        }
    }, [formMethods, open])

    const handleOk = () => {
        if (saving) return
        if (file === null) {
            alert('Ingen fil vald')
            return
        }
        setSaving(true)
        const values = formMethods.getValues() as Omit<SourceForm, 'sourceUrl'>

        const { sourceType, publishedDate } = values

        if (!sourceType) {
            alert('Ingen källtyp vald.')
            return
        }

        const newFileName = setGoodFilename(entity || {}, sourceType, subStartDate || publicationDate || publishedDate)

        docsApi
            .uploadUrl(newFileName)
            .then((response) => handleSubmit(response.uploadUrl, file, { sourceUrl: response.filename, ...values }))
            .catch((err) => {
                alert('Någonting gick fel vid uppladdningen.')
                console.error(err)
            })
            .finally(() => {
                setSaving(false)
                setOpen(false)
            })
    }

    const registeredSourceTypes = entity?.sources?.map((source) => source.sourceType).filter((t) => !!t) ?? []
    const singularSourceTypes = issue ? singularIssueSourceTypes : singularWarrantSourceTypes
    const publicationSource = entity?.sources.find((s) => s.sourceType === 'Publication')
    const minD: Date | string | null = publicationSource?.publishedDate || publicationDate || null
    const minDate = minD ? new Date(minD) : null

    return (
        <FormProvider {...formMethods}>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Ladda upp källdokument</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <input
                            type='file'
                            accept='.pdf'
                            onChange={(e: { target: HTMLInputElement }) => setFile((e.target.files as FileList)[0])}
                        />
                        <SourceTypeSelect
                            label={t('site.table.type')}
                            formMethods={formMethods}
                            setOfTypes='uploadedSource'
                            transactionType={issue?.type ? 'Issue' : null}
                            issueType={issue?.type ?? null}
                            excludeSourceTypes={intersection(singularSourceTypes, registeredSourceTypes)}
                        />
                        <RegisteredDatePicker label='Publicerad' name='publishedDate' minDate={minDate ?? undefined} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Avbryt</Button>
                    <Button onClick={handleOk} disabled={saving}>
                        OK {saving && <CircularProgress size={24} />}
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}
